import { forwardRef, InputHTMLAttributes, ReactNode, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import clsx from "clsx";

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  icon?: ReactNode;
  iconPosition?: "left" | "right";
  showPasswordToggle?: boolean;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, hint, icon, iconPosition = "left", showPasswordToggle, className, type, ...props }, ref) => {
    const [showPassword, setShowPassword] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const isPassword = type === "password";
    const inputType = isPassword && showPassword ? "text" : type;

    return (
      <div className="w-full">
        {label && (
          <label className="flex items-center gap-1.5 text-sm font-medium text-[var(--text-primary)] mb-2">
            {label}
          </label>
        )}
        <div className={clsx(
          "relative rounded-xl transition-all duration-200",
          isFocused && !error && "ring-2 ring-lime-500/20",
          isFocused && error && "ring-2 ring-rose-500/20"
        )}>
          {icon && iconPosition === "left" && (
            <div className={clsx(
              "absolute left-3 top-1/2 -translate-y-1/2 transition-colors duration-200",
              isFocused ? "text-lime-500" : "text-[var(--text-tertiary)]"
            )}>
              {icon}
            </div>
          )}
          <input
            ref={ref}
            type={inputType}
            onFocus={(e) => { setIsFocused(true); props.onFocus?.(e); }}
            onBlur={(e) => { setIsFocused(false); props.onBlur?.(e); }}
            className={clsx(
              "w-full rounded-xl border-[1.5px] transition-all duration-200",
              "bg-white/60 dark:bg-[#0d1420]/60",
              "text-[var(--text-primary)]",
              "placeholder-[var(--text-tertiary)]",
              error
                ? "border-rose-400 focus:border-rose-500"
                : "border-[var(--border)] focus:border-lime-500",
              icon && iconPosition === "left" && "pl-10",
              (icon && iconPosition === "right") || (isPassword && showPasswordToggle) ? "pr-10" : "",
              !icon && iconPosition === "left" && "px-4",
              "py-3 text-sm",
              "outline-none",
              props.disabled && "opacity-50 cursor-not-allowed",
              className
            )}
            {...props}
          />
          {icon && iconPosition === "right" && !isPassword && (
            <div className={clsx(
              "absolute right-3 top-1/2 -translate-y-1/2 transition-colors duration-200",
              isFocused ? "text-lime-500" : "text-[var(--text-tertiary)]"
            )}>
              {icon}
            </div>
          )}
          {isPassword && showPasswordToggle && (
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)] hover:text-lime-500 transition-colors"
            >
              {showPassword ? (
                <EyeOff size={20} />
              ) : (
                <Eye size={20} />
              )}
            </button>
          )}
        </div>
        <AnimatePresence mode="wait">
          {error && (
            <motion.p
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              className="mt-1.5 text-sm text-rose-500"
            >
              {error}
            </motion.p>
          )}
          {!error && hint && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-1.5 text-xs text-[var(--text-tertiary)]"
            >
              {hint}
            </motion.p>
          )}
        </AnimatePresence>
      </div>
    );
  }
);

Input.displayName = "Input";
export default Input;
